import React, { useState, useEffect } from 'react';

import useMobileCheck from 'hooks/useMobileCheck';

import SingleVerificationInput from './SingleVerificationInput';
import styles from './VerificationCodeInput.module.scss';

const getCorrectedValue = (value: string, isNumberInput?: boolean) => {
  if (!isNumberInput) {
    return value;
  }

  return !value || /\d/.test(value) ? value : '';
};

export interface VerificationCodeInputProps {
  verificationCodeValues: string[];
  onVerificationCodeValuesChange: (values: string[]) => void;
  isNumberInput?: boolean;
  disabled?: boolean;
}

const VerificationCodeInput = ({
  isNumberInput,
  verificationCodeValues,
  onVerificationCodeValuesChange,
  disabled,
}: VerificationCodeInputProps) => {
  const isMobile = useMobileCheck();
  const [activeInput, setActiveInput] = useState(0);

  useEffect(() => {
    const isEmptyCode = verificationCodeValues.every((value) => !value);

    if (!isEmptyCode) {
      return;
    }

    setActiveInput(0);
  }, [verificationCodeValues, setActiveInput]);

  const changeInputCodeByIndex = (value: string, index: number) => {
    const newCodeValues = [...verificationCodeValues];

    newCodeValues[index] = value[0] || '';
    onVerificationCodeValuesChange(newCodeValues);
  };

  const focusInput = (inputIndex: number) => {
    setActiveInput(Math.max(Math.min(verificationCodeValues.length - 1, inputIndex), 0));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const correctedValue = getCorrectedValue(event.currentTarget.value, isNumberInput);

    if (!correctedValue) {
      return;
    }

    changeInputCodeByIndex(event.currentTarget.value, activeInput);
    focusInput(activeInput + 1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Backspace': {
        event.preventDefault();

        if (verificationCodeValues[activeInput]) {
          return changeInputCodeByIndex('', activeInput);
        }

        changeInputCodeByIndex('', activeInput - 1);

        return focusInput(activeInput - 1);
      }
      case 'Delete': {
        event.preventDefault();

        return changeInputCodeByIndex('', activeInput);
      }
      case 'ArrowLeft': {
        event.preventDefault();
        focusInput(activeInput - 1);

        return;
      }
      case 'ArrowRight': {
        event.preventDefault();
        focusInput(activeInput + 1);

        return;
      }
      default: {
        return null;
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    focusInput(index);
  };

  const handleBlur = () => setActiveInput(-1);

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedData = event.clipboardData
      .getData('text/plain')
      .trim()
      .slice(0, verificationCodeValues.length - activeInput)
      .split('');

    let nextFocusIndex = 0;
    const newCodeValues = [...verificationCodeValues];

    newCodeValues.forEach((value, index) => {
      if (index < activeInput) {
        return;
      }

      const changedValue = getCorrectedValue(pastedData.shift() || value, isNumberInput);

      if (changedValue) {
        newCodeValues[index] = changedValue;
        nextFocusIndex = index;
      }
    });

    onVerificationCodeValuesChange(newCodeValues);
    setActiveInput(Math.min(nextFocusIndex + 1, verificationCodeValues.length - 1));
  };

  return (
    <div className={styles.container}>
      {verificationCodeValues.map((value, index) => (
        <div key={index}>
          <SingleVerificationInput
            type={isMobile ? 'number' : ''}
            value={value}
            focus={activeInput === index}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={(event) => handleFocus(event, index)}
            onPaste={handlePaste}
            disabled={disabled}
            onBlur={handleBlur}
            inputClassName={styles.input}
          />
        </div>
      ))}
    </div>
  );
};

export default VerificationCodeInput;
